/**
 * Agrupamento por inventory_id (Full) — puro, sem React.
 *
 * O envio ao Full é por INVENTORY (não por SKU nem por anúncio): N anúncios
 * podem compartilhar o mesmo inventory_id (mesmo estoque físico no Full), e um
 * SKU pode ter N inventories (anúncios distintos com estoque próprio). Este
 * derive monta 1 grupo por inventory_id com:
 *
 *   - mlbs   : anúncios com esse inventory_id (fonte: ml_listings).
 *   - skus   : SKUs distintos dos anúncios + SKUs com saldo nesse inventory.
 *   - vendas : SOMA das séries dos anúncios membros (demanda do inventory).
 *   - mlStock: números do Full DESSE inventory (by_inventory do backend) —
 *              o mesmo pra todos os membros, então basta o primeiro.
 *
 * Vendas de anúncios sem inventory_id NÃO entram em grupo nenhum (não
 * compartilham estoque Full). Bling é somado por SKU membro no Selector.
 *
 * @typedef {Object} InventoryGroup
 * @property {string} inventoryId
 * @property {string[]} mlbs
 * @property {string[]} skus
 * @property {string|null} primarySku   SKU com mais unidades vendidas no range (desempate alfabético).
 * @property {Map<string, number>} dailyByDate
 * @property {Map<string, {paid: number, cancelled: number, other: number}>} orderTotalsByDate
 * @property {Object|null} mlStock      {inventory_id, total, available_quantity, not_available_quantity, not_available_detail}
 * @property {string|null} titleSample
 */
import { NormalizeSku } from '../../../../utils/ml';

function addDaily(target, source) {
    if (!source) {
        return 0;
    }
    let units = 0;
    source.forEach((qty, iso) => {
        const n = Number(qty || 0);
        units += n;
        target.set(iso, (target.get(iso) || 0) + n);
    });
    return units;
}

function addOrderTotals(target, source) {
    if (!source) {
        return;
    }
    source.forEach((bucket, iso) => {
        const current = target.get(iso) || { paid: 0, cancelled: 0, other: 0 };
        current.paid += Number(bucket?.paid || 0);
        current.cancelled += Number(bucket?.cancelled || 0);
        current.other += Number(bucket?.other || 0);
        target.set(iso, current);
    });
}

/**
 * @param {{mlListingsByMlb: Map, salesByMlb: Map, stockBySku: Object}} snapshotParts
 * @returns {Map<string, InventoryGroup>}  ordenado por inventoryId
 */
export function BuildInventoryGroups({ mlListingsByMlb, salesByMlb, stockBySku }) {
    const groups = new Map();
    const ensure = (inventoryId) => {
        if (!groups.has(inventoryId)) {
            groups.set(inventoryId, {
                inventoryId,
                mlbs: new Set(),
                skus: new Set(),
                unitsBySku: new Map(),
                dailyByDate: new Map(),
                orderTotalsByDate: new Map(),
                mlStock: null,
                titleSample: null,
            });
        }
        return groups.get(inventoryId);
    };

    // 1. Anúncios — vínculo MLB → inventory + vendas do inventory.
    mlListingsByMlb?.forEach?.((listing, mlb) => {
        const inventoryId = String(listing?.inventoryId || '').trim();
        if (!inventoryId) {
            return;
        }
        const group = ensure(inventoryId);
        group.mlbs.add(mlb);
        const sales = salesByMlb?.get(mlb) || null;
        const sku = NormalizeSku(listing?.sku || sales?.sku);
        if (sku) {
            group.skus.add(sku);
        }
        if (sales) {
            const units = addDaily(group.dailyByDate, sales.dailyByDate);
            addOrderTotals(group.orderTotalsByDate, sales.orderTotalsByDate);
            if (sku) {
                group.unitsBySku.set(sku, (group.unitsBySku.get(sku) || 0) + units);
            }
        }
        if (!group.titleSample) {
            group.titleSample = listing?.title || sales?.title || null;
        }
    });

    // 2. Saldo por inventory — inclui inventories com saldo mas sem anúncio no
    //    espelho, e fornece os números do Full de cada inventory.
    for (const [rawSku, entry] of Object.entries(stockBySku || {})) {
        const list = entry?.mercadolivre?.by_inventory;
        if (!Array.isArray(list)) {
            continue;
        }
        const sku = NormalizeSku(rawSku);
        for (const inv of list) {
            const inventoryId = String(inv?.inventory_id || '').trim();
            if (!inventoryId) {
                continue;
            }
            const group = ensure(inventoryId);
            if (sku) {
                group.skus.add(sku);
            }
            if (!group.mlStock) {
                group.mlStock = {
                    inventory_id: inventoryId,
                    total: Number(inv?.total || 0),
                    available_quantity: Number(inv?.available_quantity || 0),
                    not_available_quantity: Number(inv?.not_available_quantity || 0),
                    not_available_detail: Array.isArray(inv?.not_available_detail) ? inv.not_available_detail : [],
                };
            }
        }
    }

    // 3. Finaliza: arrays ordenados + SKU primário.
    const out = new Map();
    for (const inventoryId of [...groups.keys()].sort()) {
        const group = groups.get(inventoryId);
        const skus = [...group.skus].sort();
        let primarySku = null;
        let best = -1;
        for (const sku of skus) {
            const units = group.unitsBySku.get(sku) || 0;
            if (units > best) {
                best = units;
                primarySku = sku;
            }
        }
        out.set(inventoryId, {
            inventoryId,
            mlbs: [...group.mlbs].sort(),
            skus,
            primarySku,
            dailyByDate: group.dailyByDate,
            orderTotalsByDate: group.orderTotalsByDate,
            mlStock: group.mlStock,
            titleSample: group.titleSample,
        });
    }
    return out;
}
